import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { buildApiUrl } from '../../config/api'
import GenderIcon, { MaleIcon, FemaleIcon } from '../../components/common/GenderIcon'

const TournamentRegistrations = () => {
  const { eventId } = useParams()
  const navigate = useNavigate()
  const { token } = useAuth()
  const [event, setEvent] = useState(null)
  const [participatingAdmins, setParticipatingAdmins] = useState([])
  const [expandedAdmin, setExpandedAdmin] = useState(null)
  const [loading, setLoading] = useState(true)
  const [activeGender, setActiveGender] = useState('male')

  const API_BASE = buildApiUrl('/client-admin')

  useEffect(() => {
    loadEventDetails()
    loadRegistrations()
  }, [eventId])

  const loadEventDetails = async () => {
    try {
      const response = await fetch(`${API_BASE}/events/${eventId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      })
      if (response.ok) {
        const data = await response.json()
        setEvent(data.data || data.event)
      }
    } catch (error) {
      console.error('Error loading event:', error)
    }
  }

  const loadRegistrations = async () => {
    try {
      setLoading(true)
      const response = await fetch(`${API_BASE}/events/${eventId}/registrations`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      })
      if (response.ok) {
        const data = await response.json()
        setParticipatingAdmins(data.data?.participating_admins || [])
      }
    } catch (error) {
      console.error('Error loading registrations:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleApprove = async (playerId) => {
    try {
      const response = await fetch(`${API_BASE}/events/${eventId}/registrations/${playerId}/approve`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      })
      if (response.ok) {
        showNotification('Registration approved!', 'success')
        loadRegistrations()
      }
    } catch (error) {
      showNotification('Failed to approve', 'error')
    }
  }

  const handleReject = async (playerId) => {
    if (!confirm('Are you sure you want to reject this registration?')) return
    try {
      const response = await fetch(`${API_BASE}/events/${eventId}/registrations/${playerId}/reject`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      })
      if (response.ok) {
        showNotification('Registration rejected!', 'success')
        loadRegistrations()
      }
    } catch (error) {
      showNotification('Failed to reject', 'error')
    }
  }

  const showNotification = (message, type) => {
    const notification = document.createElement('div')
    notification.className = `message-popup ${type}`
    notification.innerHTML = `
      <i class="fas fa-${type === 'success' ? 'check-circle' : 'exclamation-circle'}"></i>
      ${message}
    `
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      padding: 1rem 1.5rem;
      border-radius: 8px;
      color: white;
      font-weight: 600;
      z-index: 10000;
      display: flex;
      align-items: center;
      gap: 10px;
      background: ${type === 'success' ? 'linear-gradient(135deg, #10b981, #059669)' : 'linear-gradient(135deg, #ef4444, #dc2626)'};
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
    `
    document.body.appendChild(notification)
    setTimeout(() => notification.remove(), 3000)
  }

  const formatDate = (dateString) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const toggleAdminExpansion = (userId) => {
    setExpandedAdmin(expandedAdmin === userId ? null : userId)
  }

  // Theme colors - uses tenant's primary color or default red for throwball
  const primaryColor = '#c00f15'
  const primaryDark = '#a00d12'
  const primaryGradient = `linear-gradient(135deg, ${primaryColor} 0%, ${primaryDark} 50%, ${primaryColor} 100%)`

  return (
    <div style={{ padding: '1.5rem 2rem', width: '100%', minHeight: '100vh', background: '#f3f4f6' }}>
      {/* Header */}
      <div style={{
        background: primaryGradient,
        padding: '1.5rem 2rem',
        color: 'white',
        borderRadius: '12px',
        marginBottom: '1.5rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
          <h1 style={{ margin: 0, fontSize: '1.75rem', fontWeight: '700', color: '#ffffff' }}>
            <i className="fas fa-users" style={{ marginRight: '12px', color: '#ffffff' }}></i>
            Tournament Registrations
          </h1>
          <button
            onClick={() => navigate('/admin/events')}
            style={{
              background: '#ffffff',
              border: 'none',
              color: '#c00f15',
              padding: '8px 18px',
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '14px',
              fontWeight: '600',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              whiteSpace: 'nowrap'
            }}
          >
            <i className="fas fa-arrow-left"></i>
            Back
          </button>
        </div>
        <p style={{ margin: 0, fontSize: '1rem', color: '#ffffff' }}>
          {event?.event_name || 'Loading...'}
        </p>
        {event && (
          <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.9rem', color: 'rgba(255,255,255,0.9)' }}>
            <i className="fas fa-calendar" style={{ marginRight: '8px', color: 'rgba(255,255,255,0.9)' }}></i>
            {formatDate(event.event_start_date)} - {formatDate(event.event_end_date)}
            <span style={{ margin: '0 15px', color: 'rgba(255,255,255,0.6)' }}>|</span>
            <i className="fas fa-map-marker-alt" style={{ marginRight: '8px', color: 'rgba(255,255,255,0.9)' }}></i>
            {event.event_venue}
          </p>
        )}
      </div>

      {/* Content */}
      <div style={{ padding: 0 }}>
        {/* Gender Tabs */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '1rem',
          marginBottom: '1.5rem',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          display: 'flex',
          gap: '1rem'
        }}>
          <button
            onClick={() => setActiveGender('male')}
            style={{
              flex: 1,
              padding: '1rem',
              border: activeGender === 'male' ? `2px solid ${primaryColor}` : '2px solid #e5e7eb',
              borderRadius: '10px',
              background: activeGender === 'male' ? `${primaryColor}10` : 'white',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.75rem',
              transition: 'all 0.2s',
              fontSize: '1rem',
              fontWeight: '600',
              color: activeGender === 'male' ? primaryColor : '#6b7280'
            }}
          >
            <MaleIcon style={{ fontSize: '1.5rem' }} />
            Male
          </button>
          <button
            onClick={() => setActiveGender('female')}
            style={{
              flex: 1,
              padding: '1rem',
              border: activeGender === 'female' ? `2px solid ${primaryColor}` : '2px solid #e5e7eb',
              borderRadius: '10px',
              background: activeGender === 'female' ? `${primaryColor}10` : 'white',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.75rem',
              transition: 'all 0.2s',
              fontSize: '1rem',
              fontWeight: '600',
              color: activeGender === 'female' ? primaryColor : '#6b7280'
            }}
          >
            <FemaleIcon style={{ fontSize: '1.5rem' }} />
            Female
          </button>
        </div>

        {/* Participating Teams/Admins List */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          overflow: 'hidden'
        }}>
          <div style={{
            padding: '1.5rem',
            borderBottom: '2px solid #e5e7eb',
            background: primaryGradient
          }}>
            <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '700', color: '#ffffff' }}>
              <i className="fas fa-users"></i> Participating Teams
            </h2>
          </div>

          <div style={{ padding: '1.5rem' }}>
            {loading ? (
              <div style={{ padding: '3rem', textAlign: 'center' }}>
                <i className="fas fa-spinner fa-spin" style={{ fontSize: '2rem', color: primaryColor }}></i>
                <p style={{ marginTop: '1rem', color: '#6b7280' }}>Loading teams...</p>
              </div>
            ) : (() => {
              // Filter admins based on selected gender
              const filteredAdmins = participatingAdmins.filter(admin => {
                // Check if admin has any members in the selected gender category
                return admin.members.some(member => {
                  const category = member.category || ''
                  if (activeGender === 'male') {
                    return category.toLowerCase().includes('male') && !category.toLowerCase().includes('female')
                  } else {
                    return category.toLowerCase().includes('female')
                  }
                })
              })

              return filteredAdmins.length === 0 ? (
                <div style={{ padding: '3rem', textAlign: 'center' }}>
                  <i className="fas fa-users-slash" style={{ fontSize: '3rem', color: '#d1d5db' }}></i>
                  <p style={{ marginTop: '1rem', color: '#6b7280', fontSize: '1.1rem' }}>
                    No {activeGender} teams registered yet
                  </p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {filteredAdmins.map((admin, index) => {
                    // Calculate gender-specific counts
                    const genderMembers = admin.members.filter(member => {
                      const category = member.category || ''
                      if (activeGender === 'male') {
                        return category.toLowerCase().includes('male') && !category.toLowerCase().includes('female')
                      } else {
                        return category.toLowerCase().includes('female')
                      }
                    })
                    const genderPlayersCount = genderMembers.filter(m => !m.member_type || m.member_type === 'player').length
                    const genderCoachesCount = genderMembers.filter(m => m.member_type === 'coach').length
                    const genderManagersCount = genderMembers.filter(m => m.member_type === 'manager').length
                    const genderOfficialsCount = genderMembers.filter(m => m.member_type === 'official').length

                    return (
                      <div key={admin.user_id} style={{
                        border: '2px solid #e5e7eb',
                        borderRadius: '12px',
                        overflow: 'hidden',
                        transition: 'all 0.2s'
                      }}>
                        <div
                          onClick={() => toggleAdminExpansion(admin.user_id)}
                          style={{
                            padding: '1.25rem',
                            background: expandedAdmin === admin.user_id ? `linear-gradient(135deg, ${primaryColor}, ${primaryDark})` : '#f9fafb',
                            cursor: 'pointer',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            transition: 'all 0.2s'
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flex: 1 }}>
                            <div style={{
                              width: '48px',
                              height: '48px',
                              borderRadius: '50%',
                              background: expandedAdmin === admin.user_id ? 'rgba(255,255,255,0.2)' : `linear-gradient(135deg, ${primaryColor}, ${primaryDark})`,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'white',
                              fontWeight: '700',
                              fontSize: '1.25rem'
                            }}>
                              {index + 1}
                            </div>
                            <div style={{ flex: 1 }}>
                              <div style={{
                                fontWeight: '700',
                                fontSize: '1.1rem',
                                color: expandedAdmin === admin.user_id ? '#ffffff' : '#1f2937',
                                marginBottom: '0.25rem'
                              }}>
                                {admin.user_name}
                              </div>
                              <div style={{ fontSize: '0.875rem', color: expandedAdmin === admin.user_id ? 'rgba(255,255,255,0.8)' : '#6b7280' }}>
                                {admin.user_email}
                              </div>
                            </div>
                            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                              <span style={{
                                padding: '0.5rem 1rem',
                                borderRadius: '20px',
                                background: expandedAdmin === admin.user_id ? 'rgba(59, 130, 246, 0.2)' : '#dbeafe',
                                color: expandedAdmin === admin.user_id ? '#ffffff' : '#1d4ed8',
                                fontSize: '0.875rem',
                                fontWeight: '600'
                              }}>
                                <i className="fas fa-users"></i> {genderPlayersCount} Players
                              </span>
                              {genderCoachesCount > 0 && (
                                <span style={{
                                  padding: '0.5rem 1rem',
                                  borderRadius: '20px',
                                  background: expandedAdmin === admin.user_id ? 'rgba(251, 191, 36, 0.2)' : '#fef3c7',
                                  color: expandedAdmin === admin.user_id ? '#ffffff' : '#d97706',
                                  fontSize: '0.875rem',
                                  fontWeight: '600'
                                }}>
                                  <i className="fas fa-chalkboard-teacher"></i> {genderCoachesCount} Coaches
                                </span>
                              )}
                              {genderManagersCount > 0 && (
                                <span style={{
                                  padding: '0.5rem 1rem',
                                  borderRadius: '20px',
                                  background: expandedAdmin === admin.user_id ? 'rgba(139, 92, 246, 0.2)' : '#ede9fe',
                                  color: expandedAdmin === admin.user_id ? '#ffffff' : '#7c3aed',
                                  fontSize: '0.875rem',
                                  fontWeight: '600'
                                }}>
                                  <i className="fas fa-user-tie"></i> {genderManagersCount} Managers
                                </span>
                              )}
                              {genderOfficialsCount > 0 && (
                                <span style={{
                                  padding: '0.5rem 1rem',
                                  borderRadius: '20px',
                                  background: expandedAdmin === admin.user_id ? 'rgba(16, 185, 129, 0.2)' : '#dcfce7',
                                  color: expandedAdmin === admin.user_id ? '#ffffff' : '#16a34a',
                                  fontSize: '0.875rem',
                                  fontWeight: '600'
                                }}>
                                  <i className="fas fa-id-badge"></i> {genderOfficialsCount} Officials
                                </span>
                              )}
                            </div>
                        <i
                          className={`fas fa-chevron-${expandedAdmin === admin.user_id ? 'up' : 'down'}`}
                          style={{
                            color: expandedAdmin === admin.user_id ? '#ffffff' : '#6b7280',
                            fontSize: '1.25rem'
                          }}
                        ></i>
                      </div>
                    </div>

                    {expandedAdmin === admin.user_id && (
                      <div style={{ padding: '1.5rem', background: '#fafbfc' }}>
                        <h3 style={{ margin: '0 0 1.5rem 0', fontSize: '1rem', fontWeight: '700', color: '#1f2937' }}>
                          Team Members ({genderMembers.length} - {activeGender.charAt(0).toUpperCase() + activeGender.slice(1)})
                        </h3>

                        {/* Group members by category (e.g., "Senior (Male)", "Senior (Female)") */}
                        {(() => {
                          // Filter members by selected gender first
                          const genderFilteredMembers = admin.members.filter(member => {
                            const category = member.category || ''
                            if (activeGender === 'male') {
                              return category.toLowerCase().includes('male') && !category.toLowerCase().includes('female')
                            } else {
                              return category.toLowerCase().includes('female')
                            }
                          })

                          // Group by category
                          const categoryMap = {}
                          genderFilteredMembers.forEach(member => {
                            const cat = member.category || 'Uncategorized'
                            if (!categoryMap[cat]) categoryMap[cat] = []
                            categoryMap[cat].push(member)
                          })

                          const categories = Object.keys(categoryMap)

                          // Helper to render a members table
                          const renderMembersTable = (members, label, labelColor, labelBg) => {
                            if (members.length === 0) return null
                            return (
                              <div style={{ marginBottom: '1.25rem' }}>
                                <div style={{
                                  fontSize: '0.85rem',
                                  fontWeight: '600',
                                  color: labelColor,
                                  marginBottom: '0.5rem',
                                  padding: '0.5rem 0.75rem',
                                  background: labelBg,
                                  borderRadius: '6px',
                                  display: 'inline-block'
                                }}>
                                  {label} ({members.length})
                                </div>
                                <div style={{ overflowX: 'auto' }}>
                                  <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '0.5rem' }}>
                                    <thead>
                                      <tr style={{ background: '#f3f4f6' }}>
                                        <th style={{ padding: '10px', textAlign: 'left', fontSize: '0.7rem', fontWeight: '600', color: '#6b7280' }}>S.No</th>
                                        <th style={{ padding: '10px', textAlign: 'left', fontSize: '0.7rem', fontWeight: '600', color: '#6b7280' }}>Reg ID</th>
                                        <th style={{ padding: '10px', textAlign: 'left', fontSize: '0.7rem', fontWeight: '600', color: '#6b7280' }}>Name</th>
                                        <th style={{ padding: '10px', textAlign: 'left', fontSize: '0.7rem', fontWeight: '600', color: '#6b7280' }}>Type</th>
                                        <th style={{ padding: '10px', textAlign: 'left', fontSize: '0.7rem', fontWeight: '600', color: '#6b7280' }}>Age</th>
                                        <th style={{ padding: '10px', textAlign: 'left', fontSize: '0.7rem', fontWeight: '600', color: '#6b7280' }}>Position</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {members.map((member, idx) => {
                                        const mType = member.member_type || 'player'
                                        const typeLabel = mType === 'coach' ? 'Coach' : mType === 'manager' ? 'Manager' : mType === 'official' ? 'Official' : 'Player'
                                        const typeColor = mType === 'coach' ? '#d97706' : mType === 'manager' ? '#7c3aed' : mType === 'official' ? '#16a34a' : '#1d4ed8'
                                        return (
                                          <tr key={idx} style={{ borderBottom: '1px solid #e5e7eb', background: 'white' }}>
                                            <td style={{ padding: '10px', color: '#6b7280', fontWeight: '600' }}>{idx + 1}</td>
                                            <td style={{ padding: '10px', fontFamily: 'monospace', fontSize: '0.8rem', color: '#4b5563' }}>
                                              {member.player_reg_id}
                                            </td>
                                            <td style={{ padding: '10px', fontWeight: '600', color: '#1f2937' }}>
                                              {member.player_name}
                                            </td>
                                            <td style={{ padding: '10px' }}>
                                              <span style={{
                                                padding: '2px 8px',
                                                borderRadius: '12px',
                                                fontSize: '0.7rem',
                                                fontWeight: '600',
                                                color: typeColor,
                                                background: typeColor + '15'
                                              }}>{typeLabel}</span>
                                            </td>
                                            <td style={{ padding: '10px', color: '#4b5563' }}>{member.age || '-'}</td>
                                            <td style={{ padding: '10px', color: '#4b5563' }}>{member.position || '-'}</td>
                                          </tr>
                                        )
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            )
                          }

                          return categories.map(cat => {
                            const members = categoryMap[cat]
                            // Detect if category contains Female (for color coding)
                            const isFemale = cat.includes('Female')
                            const catColor = isFemale ? '#db2777' : '#1d4ed8'
                            const catBg = isFemale ? '#fdf2f8' : '#eff6ff'

                            return (
                              <div key={cat} style={{
                                marginBottom: '2rem',
                                border: '1px solid #e5e7eb',
                                borderRadius: '10px',
                                overflow: 'hidden'
                              }}>
                                <div style={{
                                  padding: '0.85rem 1rem',
                                  background: catBg,
                                  borderBottom: '1px solid #e5e7eb',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.5rem'
                                }}>
                                  <GenderIcon gender={isFemale ? 'F' : 'M'} style={{ color: catColor }} />
                                  <span style={{
                                    fontSize: '1rem',
                                    fontWeight: '700',
                                    color: catColor
                                  }}>
                                    {cat}
                                  </span>
                                  <span style={{
                                    marginLeft: 'auto',
                                    fontSize: '0.8rem',
                                    color: '#6b7280',
                                    fontWeight: '500'
                                  }}>
                                    {members.length} members
                                  </span>
                                </div>
                                <div style={{ padding: '1rem' }}>
                                  {/* Separate members by type within this category */}
                                  {(() => {
                                    const players = members.filter(m => !m.member_type || m.member_type === 'player')
                                    const coaches = members.filter(m => m.member_type === 'coach')
                                    const managers = members.filter(m => m.member_type === 'manager')
                                    const officials = members.filter(m => m.member_type === 'official')

                                    return (
                                      <>
                                        {renderMembersTable(players, 'Players', '#1d4ed8', '#dbeafe')}
                                        {renderMembersTable(coaches, 'Coaches', '#d97706', '#fef3c7')}
                                        {renderMembersTable(managers, 'Managers', '#7c3aed', '#ede9fe')}
                                        {renderMembersTable(officials, 'Officials', '#16a34a', '#dcfce7')}
                                      </>
                                    )
                                  })()}
                                </div>
                              </div>
                            )
                          })
                        })()}
                        </div>
                      )}
                    </div>
                    )
                  })}
                </div>
              )
            })()}
          </div>
        </div>
      </div>
    </div>
  )
}

export default TournamentRegistrations
